<template>
  <ion-page>
    <ion-header mode="ios">
      <ion-toolbar>
        <ion-buttons slot="start">
          <ion-back-button
            default-href="#"
            class="back-btn"
            text=""
            @click="$emit('closePage')"
            :icon="chevronBackOutline"
          >
          </ion-back-button>
        </ion-buttons>
        <ion-label class="head-title ml-2">{{ $t('invoice_cn.invoice_s') }}</ion-label>
      </ion-toolbar>
    </ion-header>
    <ion-content>
      <div v-if="isOpenLoadingref" class="loading">
        <ion-spinner name="circles"></ion-spinner>
      </div>
      <div v-else class="bg-content">
        <div class="column bg-white">
          <ion-text color="primary" class="head-title mb-2"> {{ invoiceDetail.supplier_name }} </ion-text>
          <ion-text class="my-1 bold"> #{{ debitNoteDetail.debit_note_number }} </ion-text>
          <div class="d-flex justify-space-between">
            <ion-text class="text-grey">{{ $t('invoice_date') }} </ion-text>
            <ion-text class="text-grey"> {{ formatDayDate(invoiceDetail.actual_delivery_date) }} </ion-text>
          </div>
          <div class="d-flex justify-space-between mt-1">
            <ion-text class="text-grey">{{ $t('payment_term') }} </ion-text>
            <ion-text class="text-grey"> {{ paymentHistories.creditTerm }} </ion-text>
          </div>
          <ion-text color="primary" class="mt-3 mb-2 bold">
            {{ $t('invoice_cn.grand_total') }}
          </ion-text>
          <ion-text color="primary" class="mb-3 bold price_inv">
            {{ priceFormatter(currencySymbol, debitNoteDetail.amount) }}</ion-text
          >
          <div class="column bg-content ion-padding">
            <ion-text color="primary" class="bold">{{ $t('invoice_cn.bill_to') }}:</ion-text>
            <ion-text class="mt-2 mb-1">{{ invoiceDetail.billing_name }}</ion-text>
            <ion-text>{{ invoiceDetail.billing_address }}</ion-text>
          </div>
          <ion-grid class="w-100 mx-0 px-0">
            <ion-row v-if="isFinanceModuleEnabled">
              <ion-col size="12">
                <ion-text
                  class="fw-bold"
                  :class="{
                    'text-primary-orange-500':
                      paymentHistories.paymentStatus === PAYMENT_HISTORY_STATUS.PARTIAL_PAID,
                    'text-primary-green-500': paymentHistories.paymentStatus === PAYMENT_HISTORY_STATUS.PAID,
                    'text-error-500': paymentHistories.paymentStatus === PAYMENT_HISTORY_STATUS.UNPAID
                  }"
                  >{{ paymentHistories.paymentStatus.toUpperCase() }}</ion-text
                >
              </ion-col>
            </ion-row>
            <ion-row>
              <ion-col size="6">
                <ion-text>{{ $t('invoice_total') }}</ion-text>
              </ion-col>
              <ion-col size="6" v-if="isFinanceModuleEnabled">
                <ion-text
                  :class="{
                    'text-error-500': paymentHistories.paymentStatus !== PAYMENT_HISTORY_STATUS.PAID
                  }"
                  class="fw-500"
                  >{{ $t('amount_due') }}</ion-text
                >
              </ion-col>
            </ion-row>
            <ion-row>
              <ion-col size="6">
                <ion-text class="fs-5 fw-bold">{{ paymentHistories.display_amount }}</ion-text>
              </ion-col>
              <ion-col size="6" v-if="paymentHistories.paymentStatus !== PAYMENT_HISTORY_STATUS.PAID">
                <ion-row v-if="isFinanceModuleEnabled">
                  <ion-text
                    :class="{
                      'text-error-500': paymentHistories.paymentStatus !== PAYMENT_HISTORY_STATUS.PAID
                    }"
                    class="fw-bold fs-5"
                    >{{ paymentHistories.display_amount_due }}</ion-text
                  >
                </ion-row>
              </ion-col>
              <ion-col v-else>
                <ion-text class="fw-bold fs-5"> - </ion-text>
              </ion-col>
            </ion-row>
            <ion-row class="align-center space-between bold mt-2">
              <ion-button
                @click="openPaymentHistory(paymentHistories)"
                mode="ios"
                fill="outline"
                color="primary"
                class="w-100 h-3rem"
                :disabled="
                  paymentHistories.paymentStatus === PAYMENT_HISTORY_STATUS.UNPAID || !isFinanceModuleEnabled
                "
              >
                {{ $t('view_payment_history') }}
                <ion-icon color="primary" class="f-icon" :icon="chevronForwardOutline"></ion-icon>
              </ion-button>
            </ion-row>
          </ion-grid>
          <div class="spacer"></div>
          <ion-row class="my-2">
            <ion-text class="fw-bold text-gray-600">{{ $t('items').toUpperCase() }}</ion-text>
          </ion-row>
          <div class="line"></div>
          <div class="my-3">
            <ion-row
              v-for="item in debitNoteDetail.debitNoteItems"
              :key="item.id"
              class="align-start row mb-2"
            >
              <ion-col>
                <ion-text>{{ item.orderItem.product_name }}</ion-text>
              </ion-col>
              <div class="column text-right">
                <ion-text>x{{ item.quantity }}</ion-text>
                <ion-text color="primary">{{ priceFormatter(currencySymbol, item.total_price) }}</ion-text>
              </div>
            </ion-row>
          </div>
          <div class="spacer"></div>
          <ion-row class="my-2">
            <ion-text class="fw-bold text-gray-600">{{ $t('summary').toUpperCase() }}</ion-text>
          </ion-row>
          <div class="line"></div>
          <ion-row
            v-if="!isAllItemsAreTaxInclusive || Number(debitNoteDetail.delivery_fee) > 0"
            class="align-center space-between mt-2 fw-600"
          >
            <ion-text class="text-grey">
              {{ $t('invoice_cn.sub_total') }}
              <span class="ion-text-lowercase" v-if="isAllItemsAreTaxInclusive">
                ({{ $t('tax_inclusive') }})
              </span>
            </ion-text>
            <ion-text color="primary">{{
              priceFormatter(
                currencySymbol,
                isAllItemsAreTaxInclusive
                  ? debitNoteDetail.sub_total + debitNoteDetail.sub_total_tax
                  : debitNoteDetail.sub_total
              )
            }}</ion-text>
          </ion-row>
          <ion-row class="align-center space-between mt-2 fw-600" v-if="!isPickupOrder">
            <ion-text class="text-grey">
              {{ $t('delivery_fees') }}
              <span class="ion-text-lowercase" v-show="debitNoteDetail.delivery_fee > 0">
                ({{
                  debitNoteDetail.delivery_fee_is_tax_inclusive ? $t('tax_inclusive') : $t('tax_exclusive')
                }})
              </span>
            </ion-text>
            <ion-text color="primary">{{
              priceFormatter(currencySymbol, debitNoteDetail.delivery_fee)
            }}</ion-text>
          </ion-row>
          <ion-row v-if="!isAllItemsAreTaxInclusive" class="align-center space-between mt-2 fw-600">
            <ion-text class="text-grey">{{ $t('invoice_cn.tax') }}</ion-text>
            <ion-text color="primary">{{
              priceFormatter(currencySymbol, debitNoteDetail.total_tax)
            }}</ion-text>
          </ion-row>
          <ion-row class="align-center space-between mt-2 fw-600">
            <ion-text>{{ $t('grand_total') }}</ion-text>
            <ion-text>{{ priceFormatter(currencySymbol, debitNoteDetail.amount) }}</ion-text>
          </ion-row>
          <div class="line my-2"></div>
          <ion-row
            class="align-center space-between text-primary-green-500 fw-600"
            v-if="paymentHistories.paymentStatus !== PAYMENT_HISTORY_STATUS.UNPAID"
          >
            <ion-text>{{ $t('paid_amount') }}</ion-text>
            <ion-text v-if="!paymentHistories.customerCreditUsed"
              >-{{ paymentHistories.display_paid_amount }}</ion-text
            >
            <ion-text v-else
              >-{{
                priceFormatter(
                  currencySymbol,
                  paymentHistories.paidAmount - paymentHistories.customerCreditUsed
                )
              }}</ion-text
            >
          </ion-row>
          <ion-row
            class="align-center space-between mt-2 text-primary-green-500 fw-600"
            v-if="
              [PAYMENT_HISTORY_STATUS.PARTIAL_PAID, PAYMENT_HISTORY_STATUS.PAID].includes(
                paymentHistories.paymentStatus
              )
            "
          >
            <ion-text>{{ $t('applied_credits') }}</ion-text>
            <ion-text>-{{ priceFormatter(currencySymbol, paymentHistories.customerCreditUsed) }}</ion-text>
          </ion-row>
          <div
            class="line my-2"
            v-if="paymentHistories.paymentStatus !== PAYMENT_HISTORY_STATUS.UNPAID"
          ></div>
          <ion-row
            class="align-center space-between bold"
            v-if="paymentHistories.paymentStatus !== PAYMENT_HISTORY_STATUS.PAID"
          >
            <ion-text>{{ $t('amount_due').toUpperCase() }}</ion-text>
            <ion-text>{{ paymentHistories.display_amount_due }}</ion-text>
          </ion-row>
        </div>
      </div>
    </ion-content>
    <ion-footer v-if="!isOpenLoadingref">
      <ion-toolbar>
        <div class="column mx-2">
          <ion-text class="my-2">{{ $t('invoice_cn.view_document') }}</ion-text>
          <ion-button @click="handleDownloadInvoice" class="invoice-btn mb-2" color="primary">
            <ion-spinner v-if="isDownloadInvoice" name="dots"></ion-spinner>
            <ion-text v-else> {{ $t('invoice_cn.invoice_s') }} (PDF) </ion-text>
          </ion-button>
        </div>
      </ion-toolbar>
    </ion-footer>
    <ion-modal mode="ios" :is-open="isShowDocPreview" @didDismiss="setShowDocPreview(false)">
      <pdf-preview :pdfUrls="[pdfUrl]" @on-dismiss="setShowDocPreview(false)"></pdf-preview>
    </ion-modal>
    <modal-payment-history
      :isOpenPaymentHistory="isOpenPaymentHistory"
      @closeModal="setOpenPaymentHistory(false)"
      :data="paymentHistories"
      :currencySymbol="currencySymbol"
    />
  </ion-page>
</template>

<script>
import { apolloClient } from '@/main';
import { ACTIONS } from '@/modules/sale/store/payment/actions';
import PdfPreview from '@/modules/shared/components/PdfPreview';
import { ModalPaymentHistory } from '@/modules/shared/components/invoices-payment';
import { PAYMENT_HISTORY_STATUS } from '@/modules/shared/constants';
import { toastError } from '@/modules/shared/utils/';
import {
  downloadInvoice,
  downloadInvoiceByInvoiceId,
  getDebitNoteDetail,
  getInvoiceDetail
} from '@/services/shared/graphql';
import { useDateFormatter } from '@/usecases/global';
import { priceFormatter } from '@/utils';
import { Capacitor } from '@capacitor/core';
import { checkmarkOutline, chevronBackOutline, chevronForwardOutline, closeOutline } from 'ionicons/icons';
import { defineComponent, ref } from 'vue';
import { createNamespacedHelpers } from 'vuex';
const { mapActions, mapGetters } = createNamespacedHelpers('sale/payment');

export default defineComponent({
  name: 'ViewDebitNote',
  inject: ['$storage'],
  components: {
    PdfPreview,
    ModalPaymentHistory
  },
  emits: ['closePage'],
  props: {
    detailOrderId: {
      type: Number,
      default: 0
    },
    invoiceId: {
      type: Number,
      default: 0
    },
    debitNoteId: {
      type: Number,
      default: 0
    },
    currencySymbol: {
      type: String,
      default: ''
    }
  },
  setup() {
    const { formatDayDate } = useDateFormatter();
    const invoiceDetail = ref({});
    const debitNoteDetail = ref({});
    const pdfUrl = ref('');
    const isOpenLoadingref = ref(true);
    const isDownloadInvoice = ref(false);
    const isShowDocPreview = ref(false);
    const setOpenLoading = (state) => (isOpenLoadingref.value = state);
    const setOpenDownload = (state) => (isDownloadInvoice.value = state);
    const setShowDocPreview = (state) => (isShowDocPreview.value = state);
    const paymentHistories = ref(null);
    const isOpenPaymentHistory = ref(false);
    const setOpenPaymentHistory = (state) => (isOpenPaymentHistory.value = state);
    const openPaymentHistory = (histories) => {
      paymentHistories.value = histories;
      setOpenPaymentHistory(true);
    };
    return {
      formatDayDate,
      chevronBackOutline,
      chevronForwardOutline,
      checkmarkOutline,
      closeOutline,
      invoiceDetail,
      debitNoteDetail,
      isOpenLoadingref,
      setOpenLoading,
      isDownloadInvoice,
      setOpenDownload,
      pdfUrl,
      isShowDocPreview,
      setShowDocPreview,
      paymentHistories: ref({}),
      PAYMENT_HISTORY_STATUS,
      priceFormatter,
      isOpenPaymentHistory,
      setOpenPaymentHistory,
      openPaymentHistory
    };
  },
  async mounted() {
    this.setOpenLoading(true);
    const { tenant } = await this.$storage.getUser();
    await this.getInvoiceDetail(tenant.id);
    await this.getDebitNoteDetail(tenant.id);
    await this.getPaymentHistory(tenant.id);
    this.setOpenLoading(false);
  },
  computed: {
    ...mapGetters(['paymentHistory', 'error', 'isFinanceModuleEnabled']),
    isAllItemsAreTaxInclusive() {
      return (
        this.debitNoteDetail.debitNoteItems?.length &&
        this.debitNoteDetail.debitNoteItems?.every((item) => item.price_is_tax_inclusive)
      );
    },
    isPickupOrder() {
      return this.invoiceDetail.invoice_items?.some((item) => item.order_items?.order?.is_pickup);
    }
  },
  methods: {
    ...mapActions([ACTIONS.GET_PAYMENT_HISTORY]),
    async getPaymentHistory(tenantId) {
      await this[ACTIONS.GET_PAYMENT_HISTORY]({
        tenantId,
        invoiceId: this.invoiceId,
        debitNoteId: this.debitNoteDetail.id
      });
      const { paymentHistory } = this.paymentHistory;
      this.paymentHistories = paymentHistory;
    },
    async getInvoiceDetail(tenantId) {
      const { data } = await apolloClient.query({
        query: getInvoiceDetail,
        variables: {
          invoiceId: this.invoiceId,
          tenantId: tenantId
        }
      });
      this.invoiceDetail = data.getInvoiceDetail;
    },
    async getDebitNoteDetail(tenantId) {
      const { data } = await apolloClient.query({
        query: getDebitNoteDetail,
        variables: {
          debitNoteId: this.debitNoteId,
          tenantId: tenantId
        }
      });
      this.debitNoteDetail = {
        ...data.getDebitNoteDetail,
        debitNoteItems: data.getDebitNoteDetail.debitNoteItems.filter(
          (item) => !item.orderItem.is_delivery_fee
        )
      };
    },
    async handleDownloadInvoice() {
      this.setOpenDownload(true);
      try {
        const { data } = await apolloClient.mutate({
          mutation: !this.detailOrderId ? downloadInvoiceByInvoiceId : downloadInvoice,
          variables: {
            ...(!this.detailOrderId && { invoiceId: this.invoiceId }),
            ...(this.detailOrderId && { orderId: this.detailOrderId }),
            ...(this.debitNoteDetail.id && { debitNoteId: this.debitNoteDetail.id })
          }
        });
        this.pdfUrl = !this.detailOrderId ? data.downloadInvoiceByInvoiceId : data.downloadInvoice;
        if (!this.pdfUrl) {
          toastError(this.$t('invoice_cn.download_file_fail'));
          return;
        }
        if (Capacitor.isNativePlatform()) {
          this.setShowDocPreview(true);
        } else {
          await window.open(data.downloadInvoiceByInvoiceId || data.downloadInvoice, '_blank');
        }
      } catch (e) {
        toastError(this.$t('invoice_cn.download_file_fail'));
      } finally {
        this.setOpenDownload(false);
      }
    }
  }
});
</script>
<style src="./style.scss" lang="scss" scoped></style>
